import { memo, useCallback, useMemo, useState } from "react";
import { Button, Modal, Pagination } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useFindEvents } from "../../api/queries/useFindEvents";
import { useGetBookedEvents } from "../../api/queries/useGetBookedEvents";
import { useAppContext } from "../../Context/AppContext";
import { BookedEvents, Event } from "../../types/Types";

const PAGE_SIZE = 20;
const PAGINATION_THRESHOLD = 10;
const EVENT_QUESTIONS_PATH = "/event-questions";

const SPINNER_STYLE: React.CSSProperties = {
  width: "3rem",
  height: "3rem",
};

const LOADING_CONTAINER_STYLE: React.CSSProperties = {
  minHeight: "200px",
};

const CARD_ACTIONS_STYLE: React.CSSProperties = {
  minWidth: "245px",
};

interface EventCardProps {
  event: Event;
  isBooked: boolean;
  onBook: (eventId: number) => void;
  onViewBooked: (event: BookedEvents) => void;
  getBookedEvent: (eventId: number) => BookedEvents | undefined;
}

const EventCard = memo(function EventCard({
  event,
  isBooked,
  onBook,
  onViewBooked,
  getBookedEvent,
}: EventCardProps) {
  const eventDate = new Date(event.eventDate);
  const isEnded = eventDate < new Date();
  const isEarlyBirdActive =
    event.earlyBirdDateEnd && new Date(event.earlyBirdDateEnd) > new Date();

  const handleViewBookedClick = useCallback(() => {
    const bookedEvent = getBookedEvent(event.id);
    if (bookedEvent) onViewBooked(bookedEvent);
  }, [event.id, getBookedEvent, onViewBooked]);

  return (
    <div className="card shadow-lg rounded-4 border-0">
      <div className="card-body d-flex flex-column justify-content-between">
        <div className="d-flex justify-content-between flex-md-row flex-column gap-3">
          <div>
            <h5 className="card-title fw-bold">{event.name}</h5>
            <p className="card-text text-muted">{event.description}</p>
            <p className="small text-secondary">
              <strong>Location:</strong> {event.location || "N/A"}
            </p>
            <p className="small text-secondary">
              <strong>Event Date:</strong>{" "}
              {eventDate.toLocaleDateString() || "N/A"}
            </p>
          </div>
          <div className="px-3" style={CARD_ACTIONS_STYLE}>
            <ul className="list-unstyled mb-3 small">
              <li>
                <strong>Price:</strong> £{event.price}
              </li>
              <li>
                <strong>Early Bird Discount:</strong> £{event.earlyBirdPrice}
              </li>
              <li>
                {isEarlyBirdActive ? (
                  <strong>
                    Early Bird Ends On:{" "}
                    {new Date(event.earlyBirdDateEnd!).toLocaleDateString()}
                  </strong>
                ) : (
                  <span className="text-danger">Early Bird Ended</span>
                )}
              </li>
            </ul>
          </div>
        </div>
        {isEnded ? (
          <Button className="btn w-100 mt-2 btn-secondary" disabled type="button">
            Ended
          </Button>
        ) : isBooked ? (
          <Button
            className="btn w-100 mt-2 btn-success"
            onClick={handleViewBookedClick}
            type="button"
          >
            View Booked Event
          </Button>
        ) : event.seats > 0 ? (
          <Button
            className="btn w-100 mt-2 btn-primary"
            onClick={() => onBook(event.id)}
            type="button"
          >
            Book Now
          </Button>
        ) : (
          <Button className="btn w-100 mt-2 btn-danger" disabled type="button">
            Sold Out
          </Button>
        )}
      </div>
    </div>
  );
});
EventCard.displayName = "EventCard";

function Events() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<BookedEvents | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const { objectId } = useAppContext();

  const {
    data: events,
    isPending,
    error,
  } = useFindEvents({ pageSize: PAGE_SIZE, page: currentPage });
  const { data: bookedEvents } = useGetBookedEvents({ id: objectId || "" });

  const handleBook = useCallback(
    (eventId: number) => {
      navigate(`${EVENT_QUESTIONS_PATH}/${eventId}`);
    },
    [navigate]
  );

  const handleViewBooked = useCallback((event: BookedEvents) => {
    setSelectedEvent(event);
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setSelectedEvent(null);
  }, []);

  const handleCancelModalClose = useCallback(() => {
    setShowCancelModal(false);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleOpenCancelModal = useCallback(() => {
    setShowModal(false);
    setShowCancelModal(true);
  }, []);

  // Filter events by search term and city
  const filteredEvents = useMemo(() => {
    return (events?.data as unknown as Event[])?.filter((event) => {
      const matchesSearch = `${event.name} ${event.description ?? ""}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const matchesCity =
        citySearch.trim() === "" ||
        event.location?.toLowerCase().includes(citySearch.toLowerCase());

      const isUpcoming = new Date(event.eventDate) >= new Date();

      return matchesSearch && matchesCity && isUpcoming;
    });
  }, [events?.data, searchTerm, citySearch]);

  // Calculate total pages for pagination
  const totalPages = useMemo(
    () => Math.ceil((events?.totalRecords ?? 0) / PAGE_SIZE),
    [events?.totalRecords]
  );

  const isEventBooked = useCallback(
    (eventId: number) => {
      return (
        Array.isArray(bookedEvents) &&
        bookedEvents.some((bookedEvent) => bookedEvent.id === eventId)
      );
    },
    [bookedEvents]
  );

  const getBookedEvent = useCallback(
    (eventId: number) => {
      return Array.isArray(bookedEvents)
        ? bookedEvents.find((bEvent: BookedEvents) => bEvent.id === eventId)
        : undefined;
    },
    [bookedEvents]
  );

  const handleSearchTermChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  const handleCitySearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCitySearch(e.target.value);
    },
    []
  );

  const eventList = useMemo(
    () => (filteredEvents ?? []) as Event[],
    [filteredEvents]
  );

  const showPagination =
    (events?.totalRecords ?? 0) > PAGINATION_THRESHOLD;

  const handlePageFirst = useCallback(
    () => handlePageChange(1),
    [handlePageChange]
  );
  const handlePagePrev = useCallback(
    () => handlePageChange(currentPage - 1),
    [currentPage, handlePageChange]
  );
  const handlePageNext = useCallback(
    () => handlePageChange(currentPage + 1),
    [currentPage, handlePageChange]
  );
  const handlePageLast = useCallback(
    () => handlePageChange(totalPages),
    [totalPages, handlePageChange]
  );

  return (
    <main className="page-content w-100 p-4 pt-5 pt-md-5 m-0" aria-label="Events listing">
      {/* Search Inputs */}
      <div className="row mb-4 g-3">
        <div className="col-md-6">
          <input
            type="text"
            className="form-control"
            placeholder="Search events..."
            value={searchTerm}
            onChange={handleSearchTermChange}
            aria-label="Search events"
          />
        </div>

        <div className="col-md-6">
          <input
            type="text"
            className="form-control"
            placeholder="Search by city..."
            value={citySearch}
            onChange={handleCitySearchChange}
            aria-label="Search by city"
          />
        </div>
      </div>

      {/* Event List */}
      <div className="d-flex flex-column gap-4">
        {isPending ? (
          <div
            className="w-100 h-100 text-center"
            style={LOADING_CONTAINER_STYLE}
          >
            <div
              className="spinner-border text-primary text-center"
              role="status"
              style={SPINNER_STYLE}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <p className="text-danger">
            Failed to load events. Please try again later.
          </p>
        ) : eventList.length > 0 ? (
          eventList.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              isBooked={isEventBooked(event.id)}
              onBook={handleBook}
              onViewBooked={handleViewBooked}
              getBookedEvent={getBookedEvent}
            />
          ))
        ) : (
          <p className="text-muted">No events match your search.</p>
        )}
      </div>

      {/* Pagination Controls */}
      {events?.totalRecords && showPagination && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First
              onClick={handlePageFirst}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={handlePagePrev}
              disabled={currentPage === 1}
            />
            {[...Array(totalPages)].map((_, i) => (
              <Pagination.Item
                key={i + 1}
                active={i + 1 === currentPage}
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={handlePageNext}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={handlePageLast}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      {/* Booked Event Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Booked Event Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEvent && (
            <>
              <h5>{selectedEvent.name}</h5>
              <p>{selectedEvent.description}</p>
              <ul className="list-unstyled small">
                <li>
                  <strong>Price:</strong> £{selectedEvent.price}
                </li>
                <li>
                  <strong>Sectors of Interest:</strong>{" "}
                  {selectedEvent.sectorsOfInterest}
                </li>
                <li>
                  <strong>Stage of Business:</strong>{" "}
                  {selectedEvent.stageOfBusiness}
                </li>
                <li>
                  <strong>Event Objectives:</strong>{" "}
                  {selectedEvent.eventObjectives}
                </li>
                <li>
                  <strong>Location:</strong> {selectedEvent.location || "N/A"}
                </li>
              </ul>

              <div className="d-flex justify-content-center mt-3">
                <Button
                  variant="danger"
                  onClick={handleOpenCancelModal}
                  type="button"
                >
                  Cancel Event Booking
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Membership Info Modal */}
      <Modal show={showCancelModal} onHide={handleCancelModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Event Booking</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          Please email <strong>enquiries@smbf.global</strong> to cancel your
          event booking.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCancelModalClose}
            type="button"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </main>
  );
}

Events.displayName = "Events";

export default memo(Events);
